import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconClose } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import {
  KANZLEI_ONBOARDING_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument } from './useLegalDocument';
import { readOwnOnboardingStatus } from '../../utils/advisorOnboarding';
import KanzleiOnboardingDialog from './KanzleiOnboardingDialog';

/**
 * M11b-1 — Annahme-/Ablehnen-UI für offene Berater-Einladungen.
 *
 * Listet die offenen Einladungen aus {@link useAuth}.`pendingInvites`
 * (`tenant_members` mit `memberUid==uid && status=='invited'`) und bietet pro
 * Einladung „Annehmen" (invited→active) bzw. „Ablehnen" (invited→declined).
 * Die harte Durchsetzung des Read-only-Zugriffs liegt weiter in den Firestore-
 * Rules + dem PHP-`requireWrite`-Guard — dieses Panel macht den Annahme-Flow nur
 * überhaupt bedienbar.
 *
 * Rendert nichts, solange keine offene Einladung vorliegt. Der Einbindungsort ist
 * vorerst der {@link TenantSwitcher}-Dropdown (der Menü-Move auf eine eigene
 * Konto-Seite folgt in M11b-2).
 */
const PendingInvitesPanel: React.FC = () => {
  const { t } = useTranslation('common');
  const { pendingInvites, acceptInvite, declineInvite, currentUser } = useAuth();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // §5-Onboarding (B4): membershipId, für den die eigene Onboarding-Bestätigung
  // aussteht ⇒ Dialog. Nur relevant, wenn die §5-Datei freigegeben (`ready`) ist.
  const [onboardingForId, setOnboardingForId] = useState<string | null>(null);
  const onboardingDoc = useLegalDocument(KANZLEI_ONBOARDING_DOC_PATH, LEGAL_DRAFT_MARKER);

  // Nur rendern, wenn es etwas zu entscheiden gibt.
  if (pendingInvites.length === 0) {
    return null;
  }

  const runAccept = async (membershipId: string) => {
    setError(null);
    setBusyId(membershipId);
    try {
      await acceptInvite(membershipId);
    } catch (err) {
      logger.error('[PendingInvitesPanel] Failed to accept invite', err);
      setError(t('tenant.pendingActionError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleAccept = async (membershipId: string) => {
    // §5-Onboarding-Vorprüfung nur bei freigegebener §5-Datei erzwingen (sonst inert:
    // direkter Accept wie bisher). Fehlt die eigene Bestätigung ⇒ §5-Dialog VOR Accept.
    if (onboardingDoc.kind === 'ready' && currentUser?.uid) {
      setBusyId(membershipId);
      const status = await readOwnOnboardingStatus(currentUser.uid);
      setBusyId(null);
      if (!status.confirmed) {
        setError(null);
        setOnboardingForId(membershipId);
        return;
      }
    }
    await runAccept(membershipId);
  };

  const handleDecline = async (membershipId: string) => {
    setError(null);
    setBusyId(membershipId);
    try {
      await declineInvite(membershipId);
    } catch (err) {
      logger.error('[PendingInvitesPanel] Failed to decline invite', err);
      setError(t('tenant.pendingActionError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border-b border-hairline px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-faint mb-2">
        {t('tenant.pendingTitle')}
      </div>
      <ul className="space-y-3">
        {pendingInvites.map((invite) => {
          const isBusy = busyId === invite.membershipId;
          return (
            <li key={invite.membershipId} className="text-sm text-secondary">
              <p className="mb-1">
                {t('tenant.pendingInviteText', { mandant: invite.displayName })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleAccept(invite.membershipId)}
                  disabled={isBusy}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  <span className="mr-1"><IconCheck size={14} /></span>
                  {isBusy ? t('tenant.pendingWorking') : t('tenant.pendingAccept')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDecline(invite.membershipId)}
                  disabled={isBusy}
                  className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1 text-xs font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
                >
                  <span className="mr-1"><IconClose size={14} /></span>
                  {t('tenant.pendingDecline')}
                </button>
              </div>
              {/* Berufsrechtlicher Pflichthinweis (Jurist-Wortlaut, M11 W4).
                  §-Verweise StBerG/StGB bleiben in allen Sprachen erhalten. */}
              <p className="mt-2 text-[11px] leading-relaxed text-faint">
                {t('tenant.acceptLegalNotice', { mandant: invite.displayName })}
              </p>
            </li>
          );
        })}
      </ul>
      {error && <p className="mt-2 text-xs text-[var(--tone-danger-fg)]">{error}</p>}

      {/* §5-Kanzlei-Onboarding-Dialog (B4) VOR dem Accept — nur bei freigegebener
          §5-Datei und fehlender/veralteter eigener Bestätigung. */}
      {onboardingForId && (
        <KanzleiOnboardingDialog
          onConfirmed={() => {
            const id = onboardingForId;
            setOnboardingForId(null);
            void runAccept(id);
          }}
          onCancel={() => setOnboardingForId(null)}
        />
      )}
    </div>
  );
};

export default PendingInvitesPanel;
